import { useOutletContext } from "react-router-dom";
import { List, ListItem, ListItemText, Button } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';

import Page from "~/components/Page";
import { classNames, formatBillingHistoryRange, formatDate } from "~/utils/utils";
import { useTrans } from '~/hooks/useTrans';
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectAdminMessages, apiGetProjectBillingHistory, apiGetProjectKeys, apiProjectBillingHistoryCheck } from "~/providers/api";
import { IClientAdminMessages, IClientKey, IClientProject } from "~/providers/client-interfaces";
import { IOutlet, IPage } from "~/interfaces";
import { IAdminAdminMessages, IAdminKey, IAdminProject } from "~/providers/admin-interfaces";
import { IProjectBillingHistory } from "~/providers/interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import TariffWhatAbout from '~/components/TariffWhatAbout';
import PaperWithTitle from '~/components/PaperWithTitle';
import BetterTariffRequestModal from "~/modals/BetterTariffRequestModal";
import CreateNotificationModal from "~/modals/CreateNotificationModal";
import MyAlert from "~/mui/MyAlert";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { getLink } from "~/providers/route";
import KeysTableInfo from "~/tables/KeysTableInfo";
import { DASHBOARD_KEYS_COUNT } from "~/const";
import AdminSendBulkEmailModal from "~/modals/AdminSendBulkEmailModal";
import WysiwygDisplay from "~/components/WysiwygDisplay";

import "./style.less";

interface IMessageData {
	createdAt: string;
	message: string;
}

interface IState {
	loading: boolean;
	isDirty: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	billingHistory: IProjectBillingHistory;
	billingHistoryLoaded: boolean;
	showBetterTariffRequest: boolean;
	debtMsg: string;
	approvedPlanError: string;
	createNotification: boolean;
	showDebtBtn: boolean;
	keys: Array<IClientKey | IAdminKey>;
	bottomRow: string;
	setReload: boolean;
	bulkEmail: boolean;
	messageData: IMessageData;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	errorMsg: "",
	project: null,
	billingHistory: null,
	billingHistoryLoaded: false,
	showBetterTariffRequest: false,
	debtMsg: "",
	approvedPlanError: "",
	createNotification: false,
	showDebtBtn: false,
	keys: [],
	bottomRow: "",
	setReload: false,
	bulkEmail: false,
	messageData: null,
};

export default function ProjectDashboardPage({
	isInternal = false,
}: IPage) {
	const { formatCredits, trans, transProjectBusinessPlan, formatPennyPrice, formatPricePerCredit } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();

	async function onBetterTarifSave() {
		// zmeni se plan projectu
		setState(prev => ({
			...prev,
			showBetterTariffRequest: false,
			isDirty: false,
			project: {
				...prev.project,
				businessPlanType: "advanced",
			},
		}));

		// nacteme znova data projektu
		const projectData = await apiGetProject(isInternal ? "admin" : "client", params.projectId, lang);

		updateState({
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
			].join(", "),
		});
	}

	function onNotificationSave() {
		updateState({
			createNotification: false,
			isDirty: false,
		});
	}

	function getMessageData(data: IAdminAdminMessages | IClientAdminMessages): IMessageData {
		return data
			? {
				createdAt: data.createdAt,
				message: typeof data.message === "string"
					? data.message
					: data.message[lang],
			}
			: null;
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, billingData, keysData, msgData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectBillingHistory(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectKeys(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectAdminMessages(isInternal ? "admin" : "client", params.projectId, lang),
		]);
		const keysLen = keysData.noErrors ? keysData.data.length : 0;
		const moreKeysLen = keysLen > DASHBOARD_KEYS_COUNT ? keysLen - DASHBOARD_KEYS_COUNT : 0;

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...billingData.noErrors
				? {
					billingHistory: billingData.data.length ? billingData.data[0] : null,
					billingHistoryLoaded: true,
				}
				: {},
			...keysData.noErrors
				? {
					keys: keysData.data.slice(0, DASHBOARD_KEYS_COUNT),
					bottomRow: keysLen > DASHBOARD_KEYS_COUNT
						? trans("projectDashboardPage.keys.limit", {
							count: moreKeysLen,
							next: trans("projectDashboardPage.keys.next", { next: moreKeysLen }),
							keys: trans("projectDashboardPage.keys.keys", { keys: moreKeysLen }),
						})
						: "",
				}
				: {},
			...msgData.noErrors
				? {
					messageData: getMessageData(msgData.data),
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...billingData.errors,
				...keysData.errors,
			].join(", "),
		});

		// nemuzu konzumovat, mam penezenku
		if (projectData.noErrors && billingData.noErrors && projectData.data.billingMethod === "wallet" && billingData.data.length > 1) {
			// predchozi mesic
			const billingHistory = billingData.data[1];

			updateState({
				showDebtBtn: billingHistory.debt > 0 || billingHistory.transactionState === "partial",
			});
		}
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	async function showDebt() {
		const checkData = await apiProjectBillingHistoryCheck("client", params.projectId, lang);

		if (checkData.noErrors) {
			updateState({
				debtMsg: checkData.message,
				setReload: true,
			});
		} else {
			updateState({
				errorMsg: checkData.errors.join(", "),
				setReload: false,
			});
		}
	}

	function closeDebtInfo() {
		const reload = state.setReload;

		updateState({
			debtMsg: "",
			setReload: false,
		});

		if (reload) {
			loadData();
		}
	}

	function getBetterProgramClick() {
		updateState({
			...state.project.approvedAdvancedPlan === false
				? {
					approvedPlanError: trans("adminDeniedAdvancedPlan"),
				}
				: {
					showBetterTariffRequest: true,
				},
		});
	}

	function emailClick() {
		updateState({
			bulkEmail: true,
		});
	}

	async function onBulkEmailSave() {
		updateState({
			bulkEmail: false,
			isDirty: false,
		});

		const msgData = await apiGetProjectAdminMessages(isInternal ? "admin" : "client", params.projectId, lang);

		if (msgData.noErrors) {
			updateState({
				messageData: getMessageData(msgData.data),
			});
		}
	}

	/* eslint-disable no-magic-numbers */
	return <Page checkLogged={true} title={trans("projectDashboardPage.title")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("projectDashboardPage.title"))}
		checkInternalRole={isInternal ? "salesman" : "none"} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.dashboard">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ state.project && <div className="dashboardGrid">
			{ state.messageData && <PaperWithTitle title={ `${trans("admin.message")} (${formatDate(new Date(state.messageData.createdAt), isCzech)})` } error={true}>
				<WysiwygDisplay className="dashboardPage__adminMessageCont" html={state.messageData.message} />
			</PaperWithTitle>}
			<PaperWithTitle title={ trans("projectDashboardPage.apiState.title") } onClick={isInternal ? emailClick : null} icon={<EmailIcon />} className={classNames(["dashboardPage__apiCallCont", state.project.canConsume ? "" : "error-info", state.showDebtBtn ? "has-debt" : ""])}>
				<div className="dashboardPage__apiCallContItems">
					<p className={classNames(["dashboardPage__apiCallContConsumeInfo", state.project.canConsume ? "green-text-color" : "red-text-color"])}>
						{ state.project.canConsume ? trans("projectDashboardPage.apiState.enabled") : trans("projectDashboardPage.apiState.disabled") }
					</p>
					<p className={classNames(["dashboardPage__apiCallContConsumeIcons", state.project.canConsume ? "green-text-color" : "red-text-color"])}>
						{ state.project.canConsume ? <CheckCircleIcon style={{ width: "40px", height: "40px" }} /> : trans("projectDashboardPage.apiState.disabledReason", { reason: state.project.forbiddenConsumptionReason }) }
					</p>
				</div>
				{ state.showDebtBtn && <div className="dashboardPage__debtArea">
					<Button onClick={showDebt} color="success" variant="contained">
						{ trans("projectDashboardPage.testDebt") }
					</Button>
				</div> }
			</PaperWithTitle>
			<PaperWithTitle title={<span className="dashboardPage__consumptionTitle">
				{ trans("projectDashboardPage.actualMonthConsumption") }
				<span className="dashboardPage__consumptionTitleAppendix">
					{ trans("projectDashboardPage.consumption.subTitle") }
				</span>
			</span>} link={getLink("project-consumption-history", isInternal, { projectId: params.projectId })} linkTitle={ trans("menu.consumption") }>
				{ state.billingHistory && <>
					<ListItem>
						<ListItemText primary={formatBillingHistoryRange(state.billingHistory, isCzech)}
							secondary={trans("projectDashboardPage.consumption.rangeTitle")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={ formatCredits(state.billingHistory.totalConsumedCredits) } secondary={trans("projectDashboardPage.consumption.consumed")} />
					</ListItem>
					{ (state.project.billedConsumption || isInternal) && <>
						<ListItem>
							<ListItemText primary={formatCredits(state.billingHistory.creditsFreeOfCharge)} secondary={trans("billingHistoryTable.freeCredits")} />
						</ListItem>
						<ListItem>
							<ListItemText primary={formatCredits(state.billingHistory.creditsCharged)} secondary={trans("billingHistoryTable.paidCredits")} />
						</ListItem>
						<ListItem>
							<ListItemText primary={formatPennyPrice(state.billingHistory.amountTotal)} secondary={trans("projectDashboardPage.consumption.amountTotal")} />
						</ListItem>
					</> }
				</> }
				{ state.billingHistoryLoaded && !state.billingHistory && <p className="dashboardPage__pWithMargin">
					{ trans("projectDashboardPage.consumption.empty") }
				</p> }
			</PaperWithTitle>
			<PaperWithTitle title={ trans("apiKeys") } link={getLink("project-keys", isInternal, { projectId: params.projectId })} linkTitle={ trans("keys.handle") }>
				<div className="dashboardPage__apiKeysContent">
					<KeysTableInfo items={state.keys} bottomRow={state.bottomRow} />
				</div>
			</PaperWithTitle>
			<PaperWithTitle title={ trans("projectDashboardPage.financialProgram") } link={getLink("project-financial", isInternal, { projectId: params.projectId })} linkTitle={ trans("menu.financial") }>
				<div className="dashboardPage__tarifContent">
					<span className="dashboardPage__tarifContentTitle">
						{ trans("projectFinancialPage.tarif.title") }: <strong className="margin-left-large">
							{ transProjectBusinessPlan(state.project.businessPlanType) }
						</strong>
					</span>
					{ (state.project.businessPlanType === "basic" && !isInternal) && <div className="dashboardPage__betterTarifCont">
						<Button onClick={getBetterProgramClick} color="success" variant="contained" disabled={state.project.userRole === "visitor"}>
							{ trans("projectDashboardPage.getBetterProgram") }
						</Button>
						<TariffWhatAbout />
					</div> }
				</div>
				<List>
					{ state.project.pricePerCredit > 0 && <ListItem>
						<ListItemText primary={formatPricePerCredit(state.project.pricePerCredit)} secondary={trans("projectDashboardPage.thousandCreditsPrice")} />
					</ListItem> }
					{ state.project.freeCreditsPerMonth > 0 && <ListItem>
						<ListItemText primary={formatCredits(state.project.freeCreditsPerMonth)} secondary={trans("projectDashboardPage.freeMonthlyCredits")} />
					</ListItem> }
					{ state.project.fixedBillingAmount > 0 && <ListItem>
						<ListItemText primary={formatPennyPrice(state.project.fixedBillingAmount)} secondary={trans("projectDashboardPage.fixedMonthlyPrice")} />
					</ListItem> }
					<ListItem>
						<ListItemText primary={state.project.billedConsumption ? trans("projectDashboardPage.billedConsumptionOn") : trans("projectDashboardPage.billedConsumptionOff")}
							secondary={state.project.totalConsumptionLimit === null || !state.project.billedConsumption ? null : trans("projectDashboardPage.maxMothlyCredits", { credits: formatCredits(state.project.totalConsumptionLimit) })} />
					</ListItem>
				</List>
			</PaperWithTitle>
			{ isInternal && <>
				<PaperWithTitle title={ trans("projectDashboardPage.internalDesc.title") }>
					<List>
						<ListItem>
							<ListItemText primary={<WysiwygDisplay html={"internalDescription" in state.project ? state.project.internalDescription : ""} />} secondary={trans("createProject.internalDescription")} />
						</ListItem>
					</List>
				</PaperWithTitle>
			</> }
		</div> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} sx={{ marginTop: "var(--margin-large)" }} /> }
		{ state.showBetterTariffRequest && <BetterTariffRequestModal params={params} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ showBetterTariffRequest: false, isDirty: false })} onSave={onBetterTarifSave} /> }
		{ state.createNotification && <CreateNotificationModal lastUpdate={state.project.lastUpdate} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ createNotification: false, isDirty: false })} onSave={onNotificationSave} /> }
		{ state.debtMsg.length > 0 && <MyAlert title={trans("projectDashboardPage.debt.title")} onClose={closeDebtInfo} cancelCaption={trans("close")}>
			{state.debtMsg}
		</MyAlert> }
		{ state.approvedPlanError.length > 0 && <MyAlert title={trans("projectDashboardPage.getBetterProgram")} onClose={() => updateState({ approvedPlanError: "" })} cancelCaption={trans("close")}>
			{state.approvedPlanError}
		</MyAlert> }
		{ state.bulkEmail && <AdminSendBulkEmailModal projectIds={[state.project.id]} projectName={state.project.internalName} onSave={onBulkEmailSave} onClose={() => updateState({ bulkEmail: false, isDirty: false })} onChange={isDirty => updateState({ isDirty })} /> }
	</Page>;
}
